import React, { useMemo, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { getBookletResellerStatsQuery } from '../../queries/booklet';
import { updateAssociationMutation } from '../../queries/association';
import { updateTeamMutation } from '../../queries/team';
import { Booklet } from '../../types/generated.types';
import AdminResellerStatsTable from './AdminResellerStatsTable';
import { mapAssociation, mapTeam, mapTeamMember } from './utils/adminResellerStatsUtils';

export interface AdminResellerStatsData {
  resellerId: string;
  resellerType: ResellerType;
  name: string;
  sold: number;
  supportSold: number;
  afEarnings: number;
  yourEarnings: number;
  earningsToCompare: number;
  supportEarnings: number;
  email?: string;
  phone?: string;
  clearingNumber?: string;
  accountNumber?: string;
  accountOwner?: string;
  salesPeriod?: React.ReactNode;
  salesStartDate?: string | null;
  salesEndDate?: string | null;
  paidOut: number;
  isMainAssociation?: boolean;
}

export enum ResellerType {
  Association = 'Association',
  Team = 'Team',
  TeamMember = 'TeamMember'
}
interface AdminResellerStatsProps {
  booklet: Booklet;
}

const AdminResellerStats: React.FC<AdminResellerStatsProps> = ({ booklet }) => {
  const { data, loading, error, refetch } = useQuery(getBookletResellerStatsQuery, {
    variables: {
      bookletId: booklet._id,
      associationId: booklet.mainAssociationId,
      fromDate: null,
      toDate: null
    },
    skip: !booklet._id || !booklet.mainAssociationId
  });

  const [updateAssociation] = useMutation(updateAssociationMutation);
  const [updateTeam] = useMutation(updateTeamMutation);

  const handlePaidOutUpdate = useCallback(async (resellerId: string, resellerType: ResellerType, amount: number) => {
    try {
      // Find the reseller data from the appropriate array
      let resellerData: any = null;

      if (resellerType === ResellerType.Association) {
        resellerData = data?.stats?.associations?.find(item => item.association._id === resellerId)?.association;
      } else if (resellerType === ResellerType.Team) {
        resellerData = data?.stats?.teams?.find(item => item.team._id === resellerId)?.team;
      }

      if (!resellerData) return;

      const currentPaidOut = resellerData.paidOutByBooklet || [];

      // Update or add entry for this booklet
      const updatedPaidOut = [...currentPaidOut.filter((p: any) => p.bookletId !== booklet._id)];
      updatedPaidOut.push({ bookletId: booklet._id, amount });

      if (resellerType === ResellerType.Association) {
        await updateAssociation({
          variables: {
            _id: resellerId,
            name: resellerData.name,
            shopShareDomain: resellerData.shopShareDomain,
            paidOutByBooklet: updatedPaidOut
          }
        });
      } else if (resellerType === ResellerType.Team) {
        await updateTeam({
          variables: {
            _id: resellerId,
            name: resellerData.name,
            paidOutByBooklet: updatedPaidOut
          }
        });
      }

      // Refetch stats to get updated data
      await refetch();
    } catch (err) {
      console.error('Failed to update paid out amount:', err);
    }
  }, [data, booklet._id, updateAssociation, updateTeam, refetch]);

  // Beräkna totalYourEarnings för att kunna använda det för huvudföreningen
  const totalYourEarnings = data?.stats?.totals?.totalEarnings || 0;

  const stats: AdminResellerStatsData[] = useMemo(() => {
    const allAssociations = data?.stats?.associations || [];
    const teams = data?.stats?.teams || [];
    const teamMembers = data?.stats?.teamMembers || [];

    const result: AdminResellerStatsData[] = [];
    const addedTeamIds = new Set<string>();
    const addedMemberIds = new Set<string>();

    // Organisera teams och members per parent
    const teamsByAssoc = new Map<string, typeof teams>();
    teams.forEach(t => {
      const assocId = String(t.team.associationId);
      if (!teamsByAssoc.has(assocId)) teamsByAssoc.set(assocId, []);
      teamsByAssoc.get(assocId)!.push(t);
    });
    const membersByTeam = new Map<string, typeof teamMembers>();
    teamMembers.forEach(m => {
      const teamId = String(m.teamMember.teamId);
      if (!membersByTeam.has(teamId)) membersByTeam.set(teamId, []);
      membersByTeam.get(teamId)!.push(m);
    });

    // Gå igenom alla associations inklusive huvudföreningen och lägg till deras hierarki
    allAssociations.forEach((assoc, index) => {
      const isMain = index === 0;
      result.push(mapAssociation(assoc, booklet._id, totalYourEarnings, isMain));

      // Lägg till teams för denna förening (inklusive huvudföreningen)
      teamsByAssoc.get(String(assoc.association._id))?.forEach(team => {
        const teamId = String(team.team._id);
        result.push(mapTeam(team, booklet._id));
        addedTeamIds.add(teamId);

        // Lägg till members
        membersByTeam.get(teamId)?.forEach(member => {
          const memberId = String(member.teamMember._id);
          result.push(mapTeamMember(member));
          addedMemberIds.add(memberId);
        });
      });
    });

    // Edge cases: teams/members utan parent
    teams.forEach(team => {
      if (!addedTeamIds.has(String(team.team._id))) {
        result.push(mapTeam(team, booklet._id));
        membersByTeam.get(String(team.team._id))?.forEach(m => {
          if (!addedMemberIds.has(String(m.teamMember._id))) {
            result.push(mapTeamMember(m));
          }
        });
      }
    });

    return result;
  }, [data, booklet._id, totalYourEarnings]);

  if (loading) {
    return <p>Laddar statistik...</p>;
  }

  if (error) {
    console.error('Stats query error:', error);
    return <p>Fel vid laddning av statistik.</p>;
  }

  if (!stats.length) {
    return <p>Ingen försäljning ännu.</p>;
  }

  const totalSold = data.stats.totals.totalSold;
  const commissionSettings = data.stats.commissionSettings;

  return (
    <div className='admin-reseller-stats-wrapper'>
      <AdminResellerStatsTable
        stats={stats}
        commissionSettings={commissionSettings}
        bookletId={booklet._id}
        onPaidOutUpdate={handlePaidOutUpdate}
      />
      <div className="reseller-stats-table">
        <div className="table-footer">
          <span>Totalt: {totalSold} sålda häften | Huvudföreningens förtjänst: {totalYourEarnings} kr</span>
        </div>
      </div>
    </div>
  );
};

export default AdminResellerStats;
